// Wrapper générique du panel CLI, PARAMÉTRÉ par `cli`.
// Reprise fidèle de la logique du CliPanel historique (cli.tsx lignes 1650-1899)
// mais spécialisée pour un seul CLI : startCli pré-rempli, sessions filtrées,
// title depuis CLI_CONFIG, et customTabs injectés.
//
// Usage (pattern network) :
//   const ClaudeCliPanel = () => <CliPanel cli="claude" />;
//   registerPanel("cli-claude", ClaudeCliPanel);
import { Show, createSignal, createMemo, onMount, type Component, type JSX } from "solid-js";
import { PanelLayout, type SubTab } from "@/shared/panel-base";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useCliJobStream, type CliStream } from "./stream";
import { MessagesView, TerminalView } from "./views";
import { WorkspacesView } from "./workspaces";
import { GitView } from "./git";
import { SessionsView, SessionSelector } from "./launcher";
import { getCliConfig } from "./cli-config";
import type { CliSession, CliTokenMode } from "./types";
import "../cli.css";

export interface CliPanelProps {
  /** Identifiant d'adapter (claude|codex|kimi|gemini|opencode|hermes|openclaw). */
  cli: string;
}

export const CliPanel: Component<CliPanelProps> = (props) => {
  // `cli` est statique pour un panel donné (un panel dédié ne change jamais de
  // CLI). On en dérive la config via createMemo pour rester dans un scope traqué
  // (règle solid/reactivity) tout en gardant un accès cfg() partout.
  const cfg = createMemo(() => getCliConfig(props.cli));
  const client = useToolClient();
  const confirm = useConfirm();

  const [sessions, setSessions] = createSignal<CliSession[]>([]);
  const [activeId, setActiveId] = createSignal("");
  const [currentCwd, setCurrentCwd] = createSignal("");
  const [opening, setOpening] = createSignal(false);
  const [startingCli, setStartingCli] = createSignal("");
  const [globalErr, setGlobalErr] = createSignal("");

  const activeSession = createMemo(() => sessions().find((s) => s.sessionId === activeId()) || null);

  // Une seule instance de stream liée à la session active (jobId).
  const stream: CliStream = useCliJobStream(() => activeSession()?.jobId ?? null);

  onMount(async () => {
    // Recharge les sessions PTY/structurées existantes au montage, FILTRÉES par CLI.
    try {
      const r = (await client.call("cli_pty_list", {})) as { sessions?: CliSession[] };
      const all = (r.sessions || []).map((s) => ({ ...s, mode: s.mode || ("pty" as const) }));
      // Ne conserve que les sessions de ce CLI (par cli OU par command pour le PTY brut).
      const mine = all.filter((s) => s.cli === props.cli || (!s.cli && s.command === props.cli));
      if (mine.length > 0) {
        setSessions(mine);
        setActiveId(mine[mine.length - 1].sessionId);
      }
    } catch {
      // pas de sessions existantes / outil absent — silencieux
    }
  });

  // Ouvre un PTY brut TAGGÉ avec le CLI courant (pour que le filtre par CLI le
  // retrouve au prochain montage). En pratique l'utilisateur d'un panel dédié
  // utilise surtout startCli ; ce chemin sert au « + Terminal PTY » libre.
  async function openPty(command: string) {
    setOpening(true);
    setGlobalErr("");
    try {
      const cwd = currentCwd().trim();
      const params: Record<string, unknown> = { command, cols: 120, rows: 30 };
      if (cwd) params.cwd = cwd;
      const r = (await client.call("cli_pty_open", params, {
        userConfirmedAt: new Date().toISOString(),
      })) as { sessionId: string; jobId: string; mode?: string; pid?: number };
      const sess: CliSession = {
        sessionId: r.sessionId,
        jobId: r.jobId,
        mode: "pty",
        command,
        cli: props.cli, // tag du panel courant
        cwd: cwd || undefined,
        pid: r.pid,
        status: "running",
        startedAt: new Date().toISOString(),
      };
      setSessions((prev) => [...prev, sess]);
      setActiveId(r.sessionId);
    } catch (e: unknown) {
      setGlobalErr(e instanceof Error ? e.message : String(e));
    } finally {
      setOpening(false);
    }
  }

  // Démarre CE CLI via cli_start (destructif → userConfirmedAt). Le CLI est
  // PRE-REMPI avec props.cli : l'utilisateur n'a pas à le choisir.
  async function startCli(_cli: string, cwd: string, tokenMode: CliTokenMode) {
    // _cli provient du launcher mais on force props.cli (panel dédié).
    void _cli;
    setStartingCli(props.cli);
    setGlobalErr("");
    try {
      const params: Record<string, unknown> = { cli: props.cli };
      if (cwd) params.cwd = cwd;
      if (tokenMode !== "off") params.tokenMode = tokenMode;
      const r = (await client.call("cli_start", params, {
        userConfirmedAt: new Date().toISOString(),
      })) as { sessionId: string; jobId: string; mode?: string };
      const sess: CliSession = {
        sessionId: r.sessionId,
        jobId: r.jobId,
        mode: r.mode === "pty" ? "pty" : "structured",
        cli: props.cli,
        cwd: cwd || undefined,
        status: "running",
        startedAt: new Date().toISOString(),
        tokenMode,
      };
      setSessions((prev) => [...prev, sess]);
      setActiveId(r.sessionId);
    } catch (e: unknown) {
      setGlobalErr(e instanceof Error ? e.message : String(e));
    } finally {
      setStartingCli("");
    }
  }

  async function closeSession(id: string) {
    const sess = sessions().find((s) => s.sessionId === id);
    const ok = await confirm.confirm({
      title: "Fermer la session CLI",
      body: `Fermer ${sess?.cli || sess?.command || id} ? Le process distant sera arrêté.`,
      level: "warn",
      confirmLabel: "Fermer",
    });
    if (!ok) return;
    try {
      const tool = sess?.mode === "structured" ? "cli_stop" : "cli_pty_close";
      await client.call(tool, { sessionId: id }, { userConfirmedAt: new Date().toISOString() });
    } catch {
      // ignore — on retire localement de toute façon
    }
    const remaining = sessions().filter((s) => s.sessionId !== id);
    setSessions(remaining);
    if (activeId() === id) setActiveId(remaining.length ? remaining[remaining.length - 1].sessionId : "");
  }

  // Bouton « Transférer » → renvoie vers le panel Files existant (file-transfer
  // déjà livré). On évite de réimplémenter ; navigation par hash de section.
  function openFileTransfer() {
    try {
      location.hash = "#panel:files";
    } catch {
      // ignore
    }
  }

  const subtabs = createMemo((): SubTab[] => {
    const sess = activeSession();
    return [
      {
        id: "sessions",
        label: "Sessions",
        render: () => (
          <SessionsView
            sessions={sessions()}
            activeId={activeId()}
            onSelect={setActiveId}
            onClose={(id) => void closeSession(id)}
            onOpenPty={(c) => void openPty(c)}
            onStartCli={(cli, cwd, tokenMode) => void startCli(cli, cwd, tokenMode)}
            client={client}
            opening={opening()}
            startingCli={startingCli()}
            currentCwd={currentCwd}
            cli={props.cli}
            cliLabel={cfg().label}
          />
        ),
      },
      {
        id: "terminal",
        label: "Terminal",
        render: () => (
          <Show
            when={sess && sess.mode === "pty"}
            fallback={<div class="panel-empty">Sélectionnez une session PTY pour afficher le terminal.</div>}
          >
            <TerminalView session={sess!} client={client} stream={stream} />
          </Show>
        ),
      },
      {
        id: "messages",
        label: "Messages",
        render: () => (
          <Show
            when={sess}
            fallback={<div class="panel-empty">Sélectionnez une session pour voir la conversation.</div>}
          >
            <MessagesView session={sess!} stream={stream} client={client} />
          </Show>
        ),
      },
      {
        id: "workspaces",
        label: "Workspaces",
        render: () => (
          <WorkspacesView
            client={client}
            onTransfer={openFileTransfer}
            currentCwd={currentCwd}
            onSelectCwd={setCurrentCwd}
          />
        ),
      },
      {
        id: "git",
        label: "Git",
        render: () => <GitView client={client} currentCwd={currentCwd} onSelectCwd={setCurrentCwd} />,
      },
      // Custom tabs spécifiques au CLI (phase 2) — reçoivent le contexte live.
      ...(cfg().customTabs || []).map(
        (t): SubTab => ({
          id: t.id,
          label: t.label,
          render: () => t.render({ session: activeSession, stream, client }),
        }),
      ),
    ];
  });

  const toolbar = (): JSX.Element => (
    <SessionSelector
      sessions={sessions()}
      activeId={activeId()}
      onSelect={setActiveId}
      onClose={(id) => void closeSession(id)}
    />
  );

  return (
    <PanelLayout
      title={cfg().title}
      toolbar={toolbar()}
      subtabs={subtabs()}
      status={
        globalErr()
          ? { text: globalErr(), level: "danger" }
          : sessions().length === 0
            ? { text: "Aucune session", level: "warn" }
            : { text: `${sessions().length} session(s)` }
      }
    >
      <div class="panel-empty">Sélectionnez un sous-onglet.</div>
    </PanelLayout>
  );
};
